import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Award, Download, Mail, Loader2, GraduationCap, FileText,
  CheckCircle2, ChevronRight, Eye, X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

/**
 * EmployeeMyCourses — full view shown when the employee taps the
 * "Meus Cursos" menu item. Lists every certificate issued for the
 * authenticated employee with two CTAs:
 *   - Baixar (download/preview PDF in new tab)
 *   - Enviar por e-mail (defaults to the profile address; the user
 *     can override one-off in the dialog)
 */
export default function EmployeeMyCourses() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [profileEmail, setProfileEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailTarget, setEmailTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employee/courses/certificates`, {
        params: { token },
      });
      setItems(res.data?.items || []);
      setProfileEmail(res.data?.email || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar cursos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = (courseKey) => {
    const url = `${BACKEND_URL}/api/employee/courses/${courseKey}/certificate?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="my-courses-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="text-center py-16" data-testid="my-courses-empty">
        <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-base font-semibold text-slate-700">
          Você ainda não possui certificados emitidos
        </h3>
        <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Quando você concluir um curso EAD e for aprovado na avaliação,
          o certificado digital será gerado automaticamente e ficará
          disponível aqui.
        </p>
      </div>
    );
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <GraduationCap className="w-5 h-5 text-amber-600" />
        Meus Cursos
      </h2>

      <div className="space-y-3" data-testid="my-courses-list">
        {items.map((it) => (
          <motion.div
            key={it.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border border-amber-200 bg-gradient-to-r from-amber-50 to-white">
              <CardContent className="p-3.5 sm:p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg p-2 bg-amber-600/10">
                    <Award className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-amber-700">
                      Certificado emitido
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      {it.course_label}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        Nota: <strong>{it.score_pct}%</strong>
                      </span>
                      <span>Emitido em <strong>{fmtDate(it.issued_at)}</strong></span>
                      {it.doc_id && (
                        <span className="font-mono text-[10px] text-slate-500">
                          ID: {it.doc_id.slice(0, 12)}…
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewTarget(it)}
                    data-testid={`my-courses-view-${it.course_key}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                    Visualizar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => download(it.course_key)}
                    data-testid={`my-courses-download-${it.course_key}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Baixar PDF
                  </Button>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setEmailTarget(it)}
                    data-testid={`my-courses-email-${it.course_key}`}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1.5" />
                    Enviar por e-mail
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <SendByEmailDialog
        item={emailTarget}
        defaultEmail={profileEmail}
        onClose={() => setEmailTarget(null)}
      />

      <CertificatePreviewDialog
        item={previewTarget}
        token={token}
        onClose={() => setPreviewTarget(null)}
      />
    </>
  );
}


function SendByEmailDialog({ item, defaultEmail, onClose }) {
  const { token } = useAuth();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (item) setEmail(defaultEmail || "");
  }, [item, defaultEmail]);

  if (!item) return null;

  const send = async () => {
    const trimmed = (email || "").trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Informe um e-mail válido");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/employee/courses/${item.course_key}/certificate/email`,
        { email: trimmed },
        { params: { token } },
      );
      toast.success(`Certificado enviado para ${res.data?.to || trimmed}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar e-mail");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="max-w-md" data-testid="my-courses-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-amber-600" />
            Enviar certificado por e-mail
          </DialogTitle>
          <DialogDescription>
            Vamos enviar o certificado de{" "}
            <strong>{item.course_label}</strong> em PDF para o endereço
            informado abaixo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="cert-email" className="text-sm">
            E-mail de destino
          </Label>
          <Input
            id="cert-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seuemail@exemplo.com"
            autoFocus
            data-testid="my-courses-email-input"
          />
          {!defaultEmail && (
            <p className="text-[11px] text-amber-700">
              Você ainda não cadastrou um e-mail no perfil. O envio é
              uma única vez; para guardar o e-mail no cadastro, peça ao
              administrador para atualizar.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" disabled={sending} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={sending}
            onClick={send}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="my-courses-email-send-btn"
          >
            {sending
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Mail className="w-3.5 h-3.5 mr-1.5" />}
            Enviar agora
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CertificatePreviewDialog({ item, token, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) {
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    let revoked = false;
    let createdUrl = null;
    setLoading(true);
    const fetchPdf = async () => {
      try {
        const res = await axios.get(
          `${API}/employee/courses/${item.course_key}/certificate`,
          { params: { token }, responseType: "blob", timeout: 30000 },
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        createdUrl = URL.createObjectURL(blob);
        if (!revoked) setBlobUrl(createdUrl);
      } catch (e) {
        if (!revoked) toast.error(
          e.response?.data?.detail || "Falha ao carregar o certificado",
        );
        if (!revoked) onClose();
      } finally {
        if (!revoked) setLoading(false);
      }
    };
    fetchPdf();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [item, token, onClose]);

  if (!item) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl w-[96vw] h-[92vh] p-0 overflow-hidden"
        data-testid="my-courses-preview-dialog"
      >
        <DialogTitle className="sr-only">
          Visualizar certificado — {item.course_label}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Pré-visualização do certificado digital ICP-Brasil em PDF.
        </DialogDescription>
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-gradient-to-r from-amber-50 to-white">
          <Award className="w-5 h-5 text-amber-600" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
              Certificado emitido
            </div>
            <div className="text-sm font-semibold text-slate-900 truncate">
              {item.course_label}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 transition"
            data-testid="my-courses-preview-close"
            type="button"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 bg-slate-100" style={{ height: "calc(92vh - 60px)" }}>
          {loading || !blobUrl ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <iframe
              src={blobUrl}
              title={`Certificado — ${item.course_label}`}
              className="w-full h-full"
              data-testid="my-courses-preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

